import * as DateIso from "@tiicker/util/lib/date-iso";
import {
  formatCurrency,
  formatFuzzy,
  formatPercent,
} from "@tiicker/util/lib/formats";
import { GetIexQuoteQuery } from "$gql/queries/currentPricing/GetIexQuote.gen";
import { HookQueryResult } from "source/hooks";
import React from "react";

type Props = {
  className?: string;
  displayOptions?: DisplayOptions;

  quote: {
    change?: number | null | undefined;
    changePercent?: number | null | undefined;
    latestPrice?: number | null | undefined;
    latestDate?: DateIso.Type;
    timeFrame?: string;
    marketCap?: number | null | undefined;
    currency?: string | null | undefined;
  };
};

export const QuoteDisplay: React.FunctionComponent<Props> = (props: Props) => {
  const wentUp = (props.quote.change || 0) >= 0;

  const displayOptions = props.displayOptions || {};

  return (
    <div
      className={[
        props.className,
        displayOptions.displayFormat === "inline" ? "stock-inline" : "block",
        displayOptions.displayFormat === "medium" ? "medium" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <p className="stockPrice">
        {formatCurrency(props.quote.latestPrice || 0)}{" "}
        {props.quote.currency &&
          props.quote.currency.toUpperCase() !== "USD" &&
          `${props.quote.currency.toUpperCase()}`}
      </p>
      <div className="stockPriceChange">
        <span
          className={
            "stockPriceDetail" +
            " " +
            (wentUp ? "stockPriceDetailUp" : "stockPriceDetailDown")
          }
        >
          <span className="changePercent">
            {formatCurrency(props.quote.change || 0)} (
            {isFinite(props.quote.changePercent || 0)
              ? formatPercent(props.quote.changePercent || 0)
              : "—%"}
            )
          </span>

          {props.quote.latestDate && (
            <span className="dayLabel">
              {DateIso.toMarketDate(props.quote.latestDate)}
            </span>
          )}

          {props.quote.timeFrame && (
            <span className="timeFrame">{props.quote.timeFrame}</span>
          )}
        </span>
      </div>

      {props.quote.marketCap !== null && !displayOptions.hideMarketCap && (
        <div className="marketCap">
          <span>{formatFuzzy(props.quote.marketCap!)}</span>
          &nbsp;
          <span className="label">Market Cap</span>
        </div>
      )}
    </div>
  );
};

type DisplayFormat = "full" | "medium" | "inline";

type DisplayOptions = {
  displayFormat?: DisplayFormat;
  hideMarketCap?: boolean;
};

type FromQueryProps = {
  query: HookQueryResult<GetIexQuoteQuery, any>;
  options?: DisplayOptions;
};

export const QuoteDisplayFromQuery: React.FunctionComponent<FromQueryProps> = (
  props
) => {
  const quoteQuery = props.query;

  if (quoteQuery.state !== "DONE") {
    return <></>;
  }

  return (
    <>
      {quoteQuery.data.iex.quote && (
        <QuoteDisplay
          displayOptions={props.options}
          quote={quoteQuery.data.iex.quote}
        />
      )}
    </>
  );
};
